import React, { useEffect, useRef, useState } from "react";
import { FaCaretDown } from "react-icons/fa";
import { U3140_PDF_ANNOTATION_SWATCH_COLORS } from "../../U3140PdfAnnotationsPalette";

export interface U3140PdfAnnotationTextColorMenuButtonProps {
  label: string;
  color: string;
  disabled?: boolean;
  onPickColor: (hex: string) => void;
  /** Libellé du champ taille (pts). */
  fontSizeLabel?: string;
  fontSizePts?: number;
  onFontSizeChange?: (value: number) => void;
  fontSizeDisabled?: boolean;
  fontSizeMin?: number;
  fontSizeMax?: number;
  /** Contenu sous les pastilles (et la taille si présente), toujours dans le panneau déroulant. */
  panelFooter?: React.ReactNode;
}

function swatchButtonClass(active: boolean): string {
  return [
    "h-7 w-7 rounded-sm border border-gray-300 shadow-xs transition hover:scale-105 focus:outline-hidden focus:ring-2 focus:ring-blue-500/40",
    active ? "ring-2 ring-blue-600 ring-offset-1" : "",
  ].join(" ");
}

export const U3140PdfAnnotationTextColorMenuButton: React.FC<U3140PdfAnnotationTextColorMenuButtonProps> = ({
  label,
  color,
  disabled = false,
  onPickColor,
  fontSizeLabel,
  fontSizePts,
  onFontSizeChange,
  fontSizeDisabled = false,
  fontSizeMin = 6,
  fontSizeMax = 72,
  panelFooter,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, [open]);

  const preview = color && color !== "transparent" ? color : "#111827";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className="flex h-[46px] min-w-30 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-xs transition hover:border-gray-400 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span
          className="flex h-8 w-8 shrink-0 flex-col items-center justify-center gap-0.5 rounded-sm border border-gray-200 bg-white px-1"
          title={preview}
        >
          <span className="text-lg font-bold leading-none" style={{ color: preview }}>
            A
          </span>
          <span className="h-0.5 w-5 rounded-full" style={{ backgroundColor: preview }} />
        </span>
        <span className="flex-1 truncate text-left font-medium text-gray-800">{label}</span>
        <FaCaretDown className={`shrink-0 text-gray-500 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div
          className="absolute left-0 top-[calc(100%+4px)] z-10000 min-w-[220px] rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
          role="dialog"
        >
          <div className="grid grid-cols-6 gap-1.5">
            {U3140_PDF_ANNOTATION_SWATCH_COLORS.map((hex) => {
              const active = color.toLowerCase() === hex.toLowerCase();
              return (
                <button
                  key={hex}
                  type="button"
                  className={swatchButtonClass(active)}
                  style={{ backgroundColor: hex }}
                  title={hex}
                  onClick={() => {
                    onPickColor(hex);
                    setOpen(false);
                  }}
                />
              );
            })}
          </div>
          {fontSizeLabel != null &&
          fontSizePts != null &&
          onFontSizeChange != null ? (
            <label className="mt-3 flex flex-col gap-1 border-t border-gray-100 pt-3 text-xs text-gray-600">
              <span>{fontSizeLabel}</span>
              <input
                type="text"
                inputMode="decimal"
                className="w-full max-w-32 rounded-sm border px-2 py-1 text-sm"
                value={fontSizePts}
                disabled={fontSizeDisabled}
                onChange={(e) => {
                  const t = e.target.value.trim().replace(",", ".");
                  if (t === "") {
                    onFontSizeChange(fontSizeMin);
                    return;
                  }
                  const n = Number(t);
                  if (Number.isNaN(n)) {
                    return;
                  }
                  onFontSizeChange(Math.min(fontSizeMax, Math.max(fontSizeMin, n)));
                }}
              />
            </label>
          ) : null}
          {panelFooter != null ? (
            <div className="mt-3 border-t border-gray-100 pt-3">{panelFooter}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
